/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import ApiService from 'cio/services/api';
import AccountService from 'cio/services/account';
import { action } from '@ember/object';
import { task } from 'ember-concurrency';
import ENV from 'cio/config/environment';
import SnackbarService from 'cio/services/snackbar';
import ExperimentsService from 'cio/services/experiments';

type FeatureFlagsResponse = { featureFlags: Record<string, boolean> };
type FeatureFlagDisplay = { key: string; value: boolean };

type FeatureFlagsRequestData = Record<string, boolean>;
type PremiumTrialRequestData = { enabled: boolean };

export default class DebugWidget extends Component<never> {
  @service api!: ApiService;
  @service account!: AccountService;
  @service snackbar!: SnackbarService;
  @service experiments!: ExperimentsService;

  @tracked flags: FeatureFlagDisplay[] = [];

  constructor(owner: unknown, args: never) {
    super(owner, args);

    this.fetchFeatureFlags.perform();
  }

  fetchFeatureFlags = task(async () => {
    let response = await this.api.request<FeatureFlagsResponse>('feature_flags', {
      pathScope: 'account',
    });

    if (response.featureFlags == null) {
      throw new Error('Error getting feature_flags');
    }

    let uiFeatureFlags = ENV.featureFlags;

    this.flags = Object.entries({ ...uiFeatureFlags, ...response.featureFlags })
      .map(([key, value]) => ({
        key,
        value,
      }))
      .sort((a, b) => a.key.localeCompare(b.key));
  });

  submitForm = task(async (formElement: HTMLFormElement) => {
    let formData = new FormData(formElement ?? undefined);

    try {
      let submitValue: Record<string, boolean> = {};

      for (let experiment of this.userExperiments) {
        let formValue = formData.get(experiment.name) as (typeof experiment.variations)[number]['name'];
        this.experiments.storeTestVariation(experiment.name, formValue);
      }
      for (let flag of this.flags) {
        let formFlag = formData.get(flag.key);
        submitValue[flag.key] = Boolean(formFlag);
      }

      await this.api.put<FeatureFlagsRequestData>('feature_flags', {
        pathScope: 'account',
        contentType: 'application/json',
        data: submitValue,
      });

      let premiumTrial = Boolean(formData.get('premium_trial'));
      if (premiumTrial !== this.isPremiumTrial) {
        await this.api.put<PremiumTrialRequestData>('premium_trial', {
          pathScope: 'account',
          contentType: 'application/json',
          data: { enabled: premiumTrial },
        });
      }

      let accountVerified = Boolean(formData.get('account_verified'));
      if (accountVerified !== this.isAccountVerified) {
        await this.api.put<PremiumTrialRequestData>('account_verified', {
          pathScope: 'account',
          contentType: 'application/json',
          data: { enabled: accountVerified },
        });
      }

      window.location.reload();
      return;
    } catch (error) {
      // This component is for dev only, it's ok to use `alert`
      alert('Error submitting, please try again');
    }
  });

  @action
  async handleSubmit(event: SubmitEvent) {
    event.preventDefault();

    await this.submitForm.perform(event.target as HTMLFormElement);
  }

  @action
  resetForcedTestVariations() {
    this.experiments.resetForcedTestVariations();
    window.location.reload();
  }

  get isPremiumTrial() {
    return this.account.current.premiumTrial;
  }

  get isAccountVerified() {
    return this.account.verificationApproved;
  }

  get userExperiments() {
    return this.experiments.allExperiments.map((experiment) => ({
      ...experiment,
      // Used for the select dropdown
      variationsItems: experiment.variations.map((variation) => ({
        value: variation.name,
        label: variation.name,
      })),
      selectedValue: this.experiments.getExperimentVariation(experiment.name),
    }));
  }

  @action
  createSnackbar() {
    this.snackbar.add('debug.snackbar-test', 'This is a test snackbar');
  }
}
